import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, ChevronRight, Loader2 } from "lucide-react";
import { getTestResults, apiError } from "@/api";
import { mapTestSummary, formatDateTime } from "@/lib/testFormat";

/** Lists every evaluated PASS test with a link to its certificate. */
export default function CertificatesIndexPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getTestResults()
      .then((d) => setItems((d.results || []).map(mapTestSummary).filter((t) => t.result === "PASS")))
      .catch((e) => setError(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 text-xs font-mono text-zinc-500 mb-2">
          <Link to="/" className="hover:text-yellow-500 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-yellow-500">Certificates</span>
        </div>
        <h1 data-testid="certificates-heading" className="font-heading text-2xl sm:text-3xl tracking-tight font-bold text-white mb-1">
          Certificates
        </h1>
        <p className="text-sm text-zinc-400 mb-8">Candidates whose evaluated test is PASS.</p>

        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-8 h-8 text-yellow-500 animate-spin mx-auto" /></div>
        ) : error ? (
          <p role="alert" className="text-sm text-red-400">{error}</p>
        ) : !items.length ? (
          <div data-testid="certificates-empty" className="bg-zinc-900 border border-white/10 rounded-xl p-12 text-center text-sm text-zinc-400">
            No passed tests yet. Evaluate a test on the Test page first.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {items.map((t) => (
              <Link
                key={t.id}
                to={`/certificate/${t.id}`}
                data-testid={`certificate-link-${t.id}`}
                className="bg-zinc-900 border border-white/10 rounded-xl p-5 hover:border-yellow-500/40 transition-colors flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-medium truncate">{t.name}</div>
                  <div className="text-xs font-mono text-zinc-500">{t.id} · {t.testCenter || "—"}</div>
                  <div className="text-xs text-zinc-600">{t.startMs ? formatDateTime(t.startMs) : ""}</div>
                </div>
                <div className="text-xl font-mono font-bold text-emerald-400">{t.overallScore}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
